/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    isPaginationResponse,
    PaginationResponse
} from '../../typings/pagination';
import { Table } from '../Table';
import * as S from './PowerTable.styled';
import { useCurrentOrganizationId } from 'context/User';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { PowerTableProps } from './PowerTable.types';
import { CenterContainer } from 'ui/Styled/Styled';
import { IdProps, RowProps } from 'ui/Table/Table.types';
import { PopupChildren, PopupContext } from 'components/Popup';
import { useToggle } from 'hooks/booleans';
import { pluralize, upperCaseFirst } from 'utils/strings';
import { SiteHeader } from 'components/Container/Container';
import {
    USE_VIEW_TRANSITION,
    DEFAULT_PAGINATION_PAGE_SIZE,
    PAGINATION_PAGE_QUERY_PARAM
} from 'helpers/constants';

import deleteIcon from 'assets/trash.png';
import { Icon } from 'ui/Icon';
import { useSearchParams } from 'react-router-dom';
export function PowerTable<
    RowData extends IdProps<IdValue, EnabledColumn>,
    IdColumn extends keyof RowData = 'id',
    IdValue extends RowData[IdColumn] = RowData[IdColumn],
    FiltersValues extends Record<string, unknown> = Record<string, unknown>,
    EnabledColumn extends keyof RowData | undefined = undefined
>({
    columns,
    getter,
    search = true,
    onDelete,
    onDownload,
    deleteWords,
    linkGetter,
    heading,
    headingRightContent: rawHeadingRightContent,
    defaultEditMode,
    selectLabel = 'Выбрать',
    deleteCoreWord = 'удалить',
    pagination,
    ...tableProps
}: PowerTableProps<
    RowData,
    IdColumn,
    IdValue,
    FiltersValues,
    EnabledColumn
>): JSX.Element {
    const searchRef = useRef('');
    const lastSearchedRef = useRef('');
    const [response, setResponse] = useState<
        PaginationResponse<RowData> | RowData[]
    >();
    const firstLoading = useRef(true);
    const [loading, setLoading] = useState(true);
    const timer = useRef<NodeJS.Timeout>();
    const organizationId = useCurrentOrganizationId();
    const [filtersValues, setFiltersValues] = useState<FiltersValues>();
    const transitionRef = useRef<any>();
    const [searchParams, setSearchParams] = useSearchParams();

    const defaultPage = Number(
        searchParams.get(PAGINATION_PAGE_QUERY_PARAM) ?? '1'
    );

    const paginationPage = useRef(defaultPage);
    const [paginationStatePage, setPaginationStatePage] = useState<string>(
        defaultPage.toString()
    );

    const [items, paginationData] = useMemo(() => {
        if (!response) {
            return [[], undefined];
        }

        if (isPaginationResponse(response)) {
            return [response.items, response];
        }

        return [response, undefined];
    }, [response]);

    const loadRows = useCallback(async () => {
        let result;

        const changeRows = async () => {
            const paginationProps = pagination
                ? {
                      pageNumber: paginationPage.current,
                      pageSize: DEFAULT_PAGINATION_PAGE_SIZE
                  }
                : undefined;

            const rows = await getter({
                search: searchRef.current,
                filters: filtersValues,
                pagination: paginationProps
            });
            setResponse(rows);
            lastSearchedRef.current = searchRef.current;
            setLoading(false);

            firstLoading.current = false;

            result = rows;
        };

        if (!USE_VIEW_TRANSITION || firstLoading.current) {
            await changeRows();
            return result;
        } else {
            transitionRef.current?.skipTransition();
            transitionRef.current = document.startViewTransition(changeRows);
            await transitionRef.current.finished;
            return result;
        }
    }, [pagination, getter, filtersValues]);

    const refreshResults = useCallback(
        async (force = false) => {
            if (
                force ||
                (!loading && lastSearchedRef.current !== searchRef.current)
            ) {
                setLoading(true);
                loadRows();
            }
        },
        [loadRows, loading]
    );

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useUpdateEffect(() => {
        refreshResults(true);
    }, [organizationId]);

    const changeSearchValue = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            searchRef.current = e.target.value;

            if (timer.current) {
                clearTimeout(timer.current);
            }

            timer.current = setTimeout(refreshResults, 1000);

            return () => {
                clearTimeout(timer.current);
            };
        },
        [refreshResults]
    );

    const onSearchKeyDown = useCallback(
        ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
            if (key === 'Enter') {
                refreshResults();
            }
        },
        [refreshResults]
    );

    const [editMode, setEditMode] = useState(defaultEditMode ?? false);

    useUpdateEffect(() => {
        setEditMode(defaultEditMode ?? false);
    }, [defaultEditMode]);

    const [selectedRows, setSelectedRows] = useState<IdValue[]>([]);

    const toggleEditMode = useToggle(setEditMode);

    const { open } = useContext(PopupContext);

    const afterRowContent = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (id: IdValue, _row: RowProps<RowData>) => {
            const deleteHandler = () => {
                if (onDelete) {
                    const onClick = () => onDelete([id]);

                    const entityString = deleteWords?.[1] ?? deleteWords?.[0];

                    const popupContent: PopupChildren = ({ close }) => (
                        <CenterContainer>
                            <S.DeletePopupTitle>
                                {entityString ? (
                                    <>
                                        Вы действительно хотите {deleteCoreWord}{' '}
                                        {entityString}?
                                    </>
                                ) : (
                                    <>Вы подтверждате удаление?</>
                                )}
                            </S.DeletePopupTitle>
                            <S.DeletePopupButton
                                view="delete"
                                onClick={() => {
                                    onClick().then(() => {
                                        close();
                                        refreshResults(true);
                                    });
                                }}
                            >
                                {upperCaseFirst(deleteCoreWord)}
                            </S.DeletePopupButton>
                        </CenterContainer>
                    );

                    open(popupContent);
                }
            };

            // TODO: Доработать кейс с горизонтальным скроллом и поиском. Возможно нужно через JS показывать иконку и уменьшать размер строки при наведении

            return (
                !search && (
                    <>
                        {onDelete && !editMode && !search && (
                            <S.DeleteButtonContainer>
                                <S.DeleteHoverArea />
                                <S.DeleteButton onClick={deleteHandler} />
                            </S.DeleteButtonContainer>
                        )}
                    </>
                )
            );
        },
        [
            deleteWords,
            editMode,
            onDelete,
            open,
            refreshResults,
            deleteCoreWord,
            search
        ]
    );

    const deletePluralizeForms = useMemo(() => {
        return pluralize(selectedRows?.length, [
            deleteWords?.[1] ?? deleteWords?.[0] ?? 'строку',
            deleteWords?.[2] ?? deleteWords?.[0] ?? 'строки',
            deleteWords?.[0] ?? 'строк'
        ]);
    }, [deleteWords, selectedRows?.length]);

    const deleteHandler = useCallback(() => {
        if (!onDelete || !selectedRows?.length) {
            return;
        }

        const onClick = () => onDelete(selectedRows);

        const popupContent: PopupChildren = ({ close }) => (
            <CenterContainer>
                <S.DeletePopupTitle>
                    Вы действительно хотите {deleteCoreWord}{' '}
                    {deletePluralizeForms}?
                </S.DeletePopupTitle>
                <S.DeletePopupButton
                    view="delete"
                    onClick={() => {
                        onClick()
                            .then(() => {
                                close();
                                refreshResults(true);
                                setEditMode(false);
                            })
                            .catch((e) => {
                                console.error(
                                    `Error while deleting rows in PowerTable`
                                );
                                console.error(e);

                                close();
                            });
                    }}
                >
                    {upperCaseFirst(deleteCoreWord)}
                </S.DeletePopupButton>
            </CenterContainer>
        );

        open(popupContent);
    }, [
        onDelete,
        selectedRows,
        open,
        deleteCoreWord,
        deletePluralizeForms,
        refreshResults
    ]);

    const downloadHandler = useCallback(() => {
        if (!onDownload || !selectedRows?.length) {
            return;
        }

        return onDownload(selectedRows);
    }, [onDownload, selectedRows]);

    const showButtons =
        items.length > 0 && (!!onDelete || !!onDownload) && !defaultEditMode;

    const buttonsContent = useMemo(() => {
        if (!showButtons) {
            return null;
        }

        return (
            <S.SearchRowButtons>
                {editMode && (
                    <>
                        {onDelete && (
                            <S.DeleteActionButton
                                view="delete"
                                disabled={!selectedRows?.length}
                                onClick={deleteHandler}
                                title={`Удалить ${deletePluralizeForms}`}
                                iconUrl={deleteIcon}
                            />
                        )}
                        {onDownload && (
                            <S.SearchRowActionButton
                                disabled={!selectedRows?.length}
                                icon="download"
                                onClick={downloadHandler}
                            />
                        )}
                    </>
                )}
                <S.ToggleEditModeButton onClick={toggleEditMode}>
                    {editMode ? 'Назад' : selectLabel}
                </S.ToggleEditModeButton>
            </S.SearchRowButtons>
        );
    }, [
        showButtons,
        editMode,
        onDelete,
        selectedRows?.length,
        deleteHandler,
        deletePluralizeForms,
        onDownload,
        downloadHandler,
        toggleEditMode,
        selectLabel
    ]);

    const headingRightContent = useMemo(() => {
        return (
            (!!rawHeadingRightContent ||
                ((editMode || showButtons) && !search)) && (
                <>
                    {typeof rawHeadingRightContent === 'function'
                        ? rawHeadingRightContent({
                              search: searchRef.current,
                              filters: filtersValues
                          })
                        : rawHeadingRightContent}
                    {!search && buttonsContent}
                </>
            )
        );
    }, [
        rawHeadingRightContent,
        editMode,
        showButtons,
        search,
        filtersValues,
        buttonsContent
    ]);

    const pagesCount = useMemo(() => {
        return paginationData
            ? Math.ceil(
                  paginationData.totalCount / DEFAULT_PAGINATION_PAGE_SIZE
              )
            : 0;
    }, [paginationData]);

    const changePage = useCallback(
        (page: number) => {
            paginationPage.current = page;
            setPaginationStatePage(page.toString());
            loadRows();

            const params = searchParams;

            if (page === 1) {
                params.delete(PAGINATION_PAGE_QUERY_PARAM);
            } else {
                params.set(PAGINATION_PAGE_QUERY_PARAM, page.toString());
            }

            setSearchParams(params);
        },
        [loadRows, searchParams, setSearchParams]
    );

    const goFirstPage = useCallback(() => {
        if (paginationPage.current === 1) {
            return;
        }

        changePage(1);
    }, [changePage]);

    const goLastPage = useCallback(() => {
        if (!pagesCount || paginationData?.pageNumber === pagesCount) {
            return;
        }

        changePage(pagesCount);
    }, [pagesCount, paginationData?.pageNumber, changePage]);

    const goPrevPage = useCallback(() => {
        if (paginationPage.current === 1) {
            return;
        }

        changePage(paginationPage.current - 1);
    }, [changePage]);

    const goNextPage = useCallback(() => {
        if (paginationPage.current === pagesCount) {
            return;
        }

        changePage(paginationPage.current + 1);
    }, [changePage, pagesCount]);

    const onPaginationInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPaginationStatePage(e.target.value);
        },
        []
    );

    const onPaginationInputEnter = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                const value = Number(e.currentTarget.value);

                if (
                    value < Number(e.currentTarget.min) ||
                    value > Number(e.currentTarget.max)
                ) {
                    return;
                }

                changePage(value);
            }
        },
        [changePage]
    );
    return (
        <>
            {(heading || headingRightContent) && (
                <SiteHeader rightContent={headingRightContent}>
                    {heading}
                </SiteHeader>
            )}
            <S.Container
                $disableHorizontalScroll={!!onDelete && !editMode}
                $disableVerticalScroll={loading}
                $hasSearch={search}
                $hasPagination={!!(pagination && paginationData)}
            >
                {search && (
                    <S.SearchRow>
                        <S.SearchField
                            type="search"
                            placeholder="Поиск"
                            onChange={changeSearchValue}
                            onKeyDown={onSearchKeyDown}
                            readOnly={loading}
                        />
                        {buttonsContent}
                    </S.SearchRow>
                )}
                <Table
                    loading={
                        loading &&
                        (!USE_VIEW_TRANSITION || firstLoading.current)
                    }
                    columns={columns}
                    paddingTop={search}
                    afterRowContent={afterRowContent}
                    editMode={editMode}
                    selectedRows={selectedRows}
                    onRowSelect={setSelectedRows}
                    rows={items}
                    linkGetter={editMode ? undefined : linkGetter}
                    disableScroll={!!onDelete && !editMode && !search}
                    filtersValues={filtersValues}
                    onFiltersChange={setFiltersValues}
                    {...tableProps}
                />
            </S.Container>
            {pagination && paginationData && (
                <>
                    <S.PaginationContainer>
                        <S.PaginationPart>
                            {paginationPage.current > 1 && (
                                <>
                                    {paginationPage.current > 2 && (
                                        <Icon
                                            icon="doubleArrowLeft"
                                            size="s"
                                            onClick={goFirstPage}
                                        />
                                    )}
                                    <div onClick={goPrevPage}>Назад</div>
                                </>
                            )}
                        </S.PaginationPart>
                        <S.PaginationMain>
                            <S.PaginationInput
                                type="number"
                                value={paginationStatePage}
                                max={pagesCount}
                                min={1}
                                onChange={onPaginationInputChange}
                                onKeyDown={onPaginationInputEnter}
                            />
                            <div>из</div>
                            <S.PaginationPagesCount
                                $active={pagesCount > paginationPage.current}
                                onClick={goLastPage}
                            >
                                {pagesCount}
                            </S.PaginationPagesCount>
                        </S.PaginationMain>
                        <S.PaginationRightPart>
                            {paginationPage.current < pagesCount && (
                                <>
                                    <div onClick={goNextPage}>Далее</div>
                                    {paginationPage.current <
                                        pagesCount - 1 && (
                                        <Icon
                                            icon="doubleArrowRight"
                                            size="s"
                                            onClick={goLastPage}
                                        />
                                    )}
                                </>
                            )}
                        </S.PaginationRightPart>
                    </S.PaginationContainer>
                </>
            )}
        </>
    );
}
